import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { describeDevice, relativeTime } from '@/lib/format'
import { Button } from '@/ui/Button'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Active sessions — the S-D3 panel, on S-B4's endpoints.
 *
 * The server says which row is the current one (`DeviceSessionOut.current`).
 * Comparing ids client-side is not an option: the browser is never given its
 * own session id, which is the entire point of an opaque reference token.
 *
 * The current row carries the *✓ Current* badge and no button (parity
 * P-SESS-03): ending the session you are using is the account menu's *Sign
 * out*, one press away on every screen, so this panel lists only the sessions
 * it can end without ending itself.
 */
export function SessionsPanel(): JSX.Element {
  const toast = useToast()
  const now = new Date()

  const sessions = useQuery(
    (signal) => call(() => api.GET('/api/v1/auth/sessions', { signal })),
    [],
  )

  const revokeOne = useMutation((sessionId: string) =>
    call(() =>
      api.DELETE('/api/v1/auth/sessions/{session_id}', {
        params: { path: { session_id: sessionId } },
      }),
    ),
  )

  const revokeOthers = useMutation(() => call(() => api.DELETE('/api/v1/auth/sessions', {})))

  const rows = sessions.state.data?.sessions ?? []
  const others = rows.filter((row) => !row.current).length

  return (
    <section className="ar-card">
      <div className="ar-card__header">
        <h2>Active sessions</h2>
        <span className="ar-card__spacer" />
        {others > 0 ? (
          <Button
            tone="ghost"
            size="sm"
            busy={revokeOthers.pending}
            busyLabel="Signing out"
            onClick={() => {
              void (async () => {
                const result = await revokeOthers.run(undefined)
                if (!result.ok) return
                sessions.refetch()
                toast.show({
                  kind: 'success',
                  title: `Signed out ${String(result.data.revoked)} other session${
                    result.data.revoked === 1 ? '' : 's'
                  }`,
                  body: 'This device is still signed in.',
                })
              })()
            }}
          >
            Sign out everywhere else
          </Button>
        ) : null}
      </div>

      <InlineError failure={revokeOne.state.failure ?? revokeOthers.state.failure} />

      {sessions.state.status === 'loading' ? (
        <div className="ar-card__body">
          <Loading label="Loading your sessions" lines={3} />
        </div>
      ) : null}

      {sessions.state.status === 'error' ? (
        <ErrorState
          failure={sessions.state.failure}
          title="Could not load your sessions"
          onRetry={sessions.refetch}
        />
      ) : null}

      {sessions.state.status === 'ready' ? (
        <div className="ar-table-wrap" tabIndex={0}>
          <table className="ar-table">
            <thead>
              <tr>
                {/* Parity P-SESS-02: the device and last-seen columns keep
                    their words on one line; the address, which is a raw IP in
                    mono, is the column that gives. */}
                <th style={{ whiteSpace: 'nowrap' }}>Device</th>
                <th style={{ width: '100%' }}>Address</th>
                <th style={{ whiteSpace: 'nowrap' }}>Last seen</th>
                <th>
                  <span className="ar-visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <strong>{describeDevice(row.user_agent)}</strong>
                    {row.current ? (
                      <div
                        className="ar-subtle"
                        style={{ fontSize: 'var(--ar-text-caption-size)' }}
                      >
                        This device
                      </div>
                    ) : null}
                  </td>
                  <td className="ar-muted ar-mono" style={{ overflowWrap: 'anywhere' }}>
                    {row.ip ?? '—'}
                  </td>
                  <td className="ar-muted" style={{ whiteSpace: 'nowrap' }}>
                    {relativeTime(row.last_seen_at, now)}
                  </td>
                  <td className="ar-num">
                    {/*
                      SHELL-53, then parity P-SESS-03 (2026-09-13): the current
                      row carries the badge alone, as `shell.html` draws it.
                      Ending the session you are using is still one press away
                      — *Sign out* in the account menu and the drawer — so the
                      button here was a second door to the same room.
                    */}
                    {row.current ? (
                      <span className="ar-badge ar-badge--success">✓ Current</span>
                    ) : (
                      <Button
                        tone="quiet"
                        size="sm"
                        disabled={revokeOne.pending}
                        onClick={() => {
                          void (async () => {
                            const result = await revokeOne.run(row.id)
                            if (!result.ok) return
                            sessions.refetch()
                            toast.show({ kind: 'success', title: 'Session ended' })
                          })()
                        }}
                      >
                        Sign out
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}
